/**
 * Zod contracts for the connect_source catalog tool card: emit payload, resolve body, and
 * server-enriched canonical result.
 */
import { z } from "zod";

import type { CNSActionContinuationMode } from "@/models/cns";
import { parseCatalogEntityPayload } from "@/lib/toolCardSchemas/parseCatalogEntityPayload";
import { isValidPrefixedKsuid } from "@/utils/ksuidGenerator";

const SOURCE_CREDENTIAL_PREFIX = "scr_";
const SOURCE_CONNECTION_PREFIX = "scn_";

const prefixedScr = z.string().refine((id) => isValidPrefixedKsuid(id, SOURCE_CREDENTIAL_PREFIX), {
  message: "Invalid source credential public ID",
});

const prefixedScn = z.string().refine((id) => isValidPrefixedKsuid(id, SOURCE_CONNECTION_PREFIX), {
  message: "Invalid source connection public ID",
});

export const SourceConnectionFailureCodeSchema = z.enum([
  "POPUP_BLOCKED",
  "POPUP_CLOSED",
  "OAUTH_TIMEOUT",
  "OAUTH_LOGIN_REQUIRED",
  "OAUTH_ACCESS_DENIED",
  "OAUTH_AUTHORIZATION_ERROR",
  "OAUTH_TOKEN_EXCHANGE_FAILED",
  "OAUTH_INSUFFICIENT_SCOPE",
  "OAUTH_STATE_INVALID",
  "OAUTH_CALLBACK_INVALID",
  "OAUTH_UNKNOWN_REGISTRATION",
  "PLUGIN_PREPARE_FAILED",
  "PLUGIN_ENABLEMENT_FAILED",
  "PLUGIN_VERIFICATION_FAILED",
]);

export type SourceConnectionFailureCode = z.infer<typeof SourceConnectionFailureCodeSchema>;

export const ConnectSourceReasonSchema = z.enum([
  "suggest_connect",
  "user_requested",
  "reconnect",
  "auth_required",
]);

export const ConnectSourceDismissScopeSchema = z.enum([
  "conversation",
  "project",
]);

export const ConnectSourcePayloadSchema = z.object({
  pluginKey: z.string().min(1).max(64),
  connectionOptionKey: z.string().min(1).max(64),
  reason: ConnectSourceReasonSchema,
  requestedCapability: z.string().min(1).max(240).optional(),
});

export const ConnectSourceResolveSchema = z.union([
  z.object({
    outcome: z.literal("connected"),
    credentialPublicId: prefixedScr,
    connectionPublicId: prefixedScn,
  }),
  z.object({ outcome: z.literal("skipped") }),
  z.object({
    outcome: z.literal("dismissed"),
    scope: ConnectSourceDismissScopeSchema.optional(),
  }),
  z.object({
    outcome: z.literal("failed"),
    code: SourceConnectionFailureCodeSchema,
    credentialPublicId: prefixedScr,
    connectionPublicId: prefixedScn,
  }),
  z.object({
    outcome: z.literal("failed"),
    code: SourceConnectionFailureCodeSchema,
  }),
]);

export const ConnectSourceCanonicalResultSchema = z.object({
  ok: z.boolean(),
  outcome: z.enum(["connected", "skipped", "dismissed", "failed"]),
  code: z.string(),
  message: z.string(),
  provider: z.string().optional(),
  pluginKey: z.string().optional(),
  connectionOptionKey: z.string().optional(),
  scope: ConnectSourceDismissScopeSchema.optional(),
  connectionPublicId: prefixedScn.optional(),
  credentialPublicId: prefixedScr.optional(),
  sourceOAuthAttemptId: z.string().min(1).optional(),
  attemptPublicId: z.string().min(1).optional(),
  toolResultApplied: z.boolean().optional(),
  continuationClaimedAt: z.string().nullable().optional(),
  claimedAssistantMessageId: z.string().nullable().optional(),
  connectionStatus: z.enum([
    "not_connected",
    "pending",
    "connected",
    "error",
    "revoked",
    "disabled",
  ]),
  continuationMode: z
    .enum(["OPTIONAL", "EXPLICIT", "NONE", "AUTO"])
    .transform((mode) => (mode === "AUTO" ? ("OPTIONAL" as const) : mode))
    .optional(),
});

export type ConnectSourcePayload = z.infer<typeof ConnectSourcePayloadSchema>;
export type ConnectSourceReason = z.infer<typeof ConnectSourceReasonSchema>;
export type ConnectSourceDismissScope = z.infer<typeof ConnectSourceDismissScopeSchema>;
export type ConnectSourceResolve = z.infer<typeof ConnectSourceResolveSchema>;
export type ConnectSourceCanonicalResult = z.infer<typeof ConnectSourceCanonicalResultSchema>;

export type ConnectSourceToolResult = ConnectSourceResolve & {
  toolCallId: string;
  continuationMode?: CNSActionContinuationMode;
} & Partial<ConnectSourceCanonicalResult>;

export type ConnectSourceArgs = {
  entities?: Array<{
    type?: string;
    pluginKey?: string;
    connectionOptionKey?: string;
    reason?: string;
    requestedCapability?: string;
    toolInvoke?: { toolCallId?: string };
  }>;
  pluginKey?: string;
  connectionOptionKey?: string;
  reason?: string;
  requestedCapability?: string;
};

/**
 * Parses streamed or reloaded connect_source tool args into a typed payload.
 */
export function parseConnectSourcePayload(args: unknown, toolCallId?: string) {
  return parseCatalogEntityPayload(
    ConnectSourcePayloadSchema,
    "connect_source",
    args,
    toolCallId,
    (entity) => ({
      pluginKey: entity.pluginKey,
      connectionOptionKey: entity.connectionOptionKey,
      reason: entity.reason,
      requestedCapability: entity.requestedCapability,
    }),
  );
}

/**
 * Parses the canonical server receipt after Action Request submission.
 */
export function parseConnectSourceCanonicalResult(
  result: unknown,
): ConnectSourceCanonicalResult | null {
  const parsed = ConnectSourceCanonicalResultSchema.safeParse(result);
  return parsed.success ? parsed.data : null;
}

/** Reads durable OAuth correlation from canonical or transitional tool-result fields. */
export function getConnectSourceOAuthAttemptId(
  result: unknown,
): string | null {
  if (!result || typeof result !== "object") return null;
  const record = result as Record<string, unknown>;
  const value = record.sourceOAuthAttemptId ?? record.attemptPublicId;
  return typeof value === "string" && value.trim() !== "" ? value : null;
}

/**
 * Resolves the plugin key for connect_source display and actions.
 */
export function resolveConnectSourcePluginKey(
  args: unknown,
  toolCallId: string | undefined,
  result: unknown,
): string {
  const fromArgs = parseConnectSourcePayload(args, toolCallId);
  if (fromArgs.success) {
    return fromArgs.data.pluginKey;
  }
  const canonical = parseConnectSourceCanonicalResult(result);
  if (canonical?.pluginKey) {
    return canonical.pluginKey;
  }
  if (result && typeof result === "object") {
    const pluginKey = (result as Record<string, unknown>).pluginKey;
    if (typeof pluginKey === "string" && pluginKey.trim() !== "") {
      return pluginKey;
    }
  }
  return "";
}

export type ConnectSourceEmitContext = {
  pluginKey: string;
  connectionOptionKey: string;
  reason: ConnectSourceReason | null;
  requestedCapability?: string;
  hasEmitContext: boolean;
};

/**
 * Merges emit args with hydrated tool-result fields so reload/OAuth paths can render receipts.
 */
export function resolveConnectSourceEmitContext(
  args: unknown,
  toolCallId: string | undefined,
  result: unknown,
): ConnectSourceEmitContext {
  const parsed = parseConnectSourcePayload(args, toolCallId);
  const pluginKey = resolveConnectSourcePluginKey(args, toolCallId, result);
  const connectionOptionKey =
    parsed.success
      ? parsed.data.connectionOptionKey
      : typeof result === "object" && result && "connectionOptionKey" in result
        ? String((result as Record<string, unknown>).connectionOptionKey ?? "")
        : "";
  return {
    pluginKey,
    connectionOptionKey,
    reason: parsed.success ? parsed.data.reason : null,
    requestedCapability: parsed.success ? parsed.data.requestedCapability : undefined,
    hasEmitContext:
      parsed.success || Boolean(pluginKey.trim() && connectionOptionKey.trim()),
  };
}
